import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { SearchCnpjProviderInterface } from '../../domain/company/interfaces/providers/search-cnpj.provider.interface';
import { GetCnpjDetailsOutput } from '../../domain/company/dtos/get-cnpj-details.output';
import { normalizeCnpj } from '../../app/utils/cnpj.validator';
import { withoutReceitaEmptyMarker } from '../../app/utils/receita-empty-marker';

interface BrasilApiCnpjResponse {
  cnpj: string;
  razao_social: string | null;
  nome_fantasia: string | null;
  situacao_cadastral: string | number | null;
  data_inicio_atividade: string | null;
  natureza_juridica: string | null;
  cnae_fiscal_descricao: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  municipio: string | null;
  uf: string | null;
  cep: string | null;
  ddd_telefone_1: string | null;
  email: string | null;
}

export class SearchCnpjProvider implements SearchCnpjProviderInterface {
  private readonly _http = inject(HttpClient);

  async handle(cnpj: string): Promise<GetCnpjDetailsOutput> {
    const cnpjLimpo = normalizeCnpj(cnpj);

    try {
      const response = await this._http
        .get<BrasilApiCnpjResponse>(
          `https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`
        )
        .toPromise();

      if (!response) {
        return this.emptyResponse(cnpjLimpo, 'CNPJ não encontrado');
      }

      return {
        cnpj: response.cnpj || cnpjLimpo,
        // A Receita devolve `********` nos campos que não tem informado; sem descartar, o
        // marcador é autopreenchido no formulário e o do nome fantasia vira o nome da conta PJ,
        // porque é dele que sai o nome de exibição de PJ. APR-2902.
        razaoSocial: withoutReceitaEmptyMarker(response.razao_social),
        nomeFantasia: withoutReceitaEmptyMarker(response.nome_fantasia),
        situacaoCadastral: String(response.situacao_cadastral || '').trim(),
        dataAbertura: String(response.data_inicio_atividade || ''),
        naturezaJuridica: String(response.natureza_juridica || '').trim(),
        atividadePrincipal: String(response.cnae_fiscal_descricao || '').trim(),
        endereco: {
          logradouro: String(response.logradouro || '').trim(),
          numero: String(response.numero || '').trim(),
          complemento: withoutReceitaEmptyMarker(response.complemento),
          bairro: String(response.bairro || '').trim(),
          cidade: String(response.municipio || '').trim(),
          estado: String(response.uf || ''),
          cep: String(response.cep || '').replace(/\D/g, ''),
        },
        telefone: String(response.ddd_telefone_1 || '').replace(/\D/g, ''),
        email: String(response.email || '').trim().toLowerCase(),
      };
    } catch (error: any) {
      return this.emptyResponse(
        cnpjLimpo,
        error?.error?.message || 'Erro na consulta do CNPJ'
      );
    }
  }

  private emptyResponse(cnpj: string, error: string): GetCnpjDetailsOutput {
    return {
      cnpj,
      razaoSocial: '',
      nomeFantasia: '',
      situacaoCadastral: '',
      dataAbertura: '',
      naturezaJuridica: '',
      atividadePrincipal: '',
      endereco: {
        logradouro: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: '',
      },
      telefone: '',
      email: '',
      error,
    };
  }
}
